import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CipherService } from './cipher.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { localAgentFetch } from '../utils/local-agent-http.util';

/** Respuesta esperada del health check del agente local. */
export interface LocalAgentHealthResponse {
  status: 'healthy' | string;
}

/** Elemento de la ruta de certificación (cadena desde titular hasta raíz). */
export interface CertificationPathItem {
  friendlyName: string;
  subjectDN: string;
  issuerDN: string;
  thumbprint: string;
  serialNumber: string;
  notBefore: string;
  notAfter: string;
  isRoot: boolean;
}

/** Valores de source devueltos por el agente para cada certificado. */
export type LocalAgentCertificateSource = 'usb_token' | 'keystore';

/** Certificado devuelto por el agente (GET /api/certificates – almacén Windows y/o token). */
export interface LocalAgentCertificate {
  /** Origen del certificado: usb_token (token físico) o keystore (almacén Windows). */
  source?: LocalAgentCertificateSource;
  /** Si el agente lo envía, identifica el slot del token; permite filtrar solo certificados del token físico. */
  slotId?: number;
  thumbprint: string;
  friendlyName?: string;
  subjectDN?: string;
  issuerDN?: string;
  serialNumber?: string;
  notBefore?: string;
  notAfter?: string;
  keyAlgorithm?: string;
  keySize?: number;
  hasPrivateKey?: boolean;
  isValid?: boolean;
  /** Cadena ordenada desde certificado del usuario (índice 0) hasta AC Raíz (último). */
  certificationPath?: CertificationPathItem[];
}

/** Respuesta GET /api/certificates. */
export interface LocalAgentCertificatesResponse {
  success: boolean;
  certificates: LocalAgentCertificate[];
  count?: number;
  errorMessage?: string | null;
  timestamp?: string;
}

/** Documento para firma PAdES con token físico (POST …/api/sign/fisico/pdf). */
export interface LocalAgentPdfDocument {
  pdfBase64: string;
  signReason?: string;
  signLocation?: string;
  /** Campos para sello visible: zona y texto/sello por defecto o imagen */
  signFieldName?: string;
  lowerLeftX?: number;
  lowerLeftY?: number;
  upperRightX?: number;
  upperRightY?: number;
  signPage?: number;
  contentSignature?: string;
  renderingMode?: string;
  /** Solo si el usuario sube su propia imagen (PNG/JPG en Base64) */
  signatureImageBase64?: string;
  /** Validación de imagen del sello visible (alineado con backend /api/sign/pdf) */
  imageValidation?: boolean;
}

/** Respuesta POST …/api/sign/fisico/pdf. */
export interface LocalAgentSignPdfResponse {
  success: boolean;
  results: Array<{ signedPdfBase64?: string; errorCode?: string; errorMessage?: string }>;
  errorMessage?: string;
}

/** Documento para firma CAdES con token físico (POST …/api/sign/fisico/cades). */
export interface LocalAgentCadesDocument {
  contentBase64: string;
  fileName?: string;
}

/** Respuesta POST …/api/sign/fisico/cades. */
export interface LocalAgentSignCadesResponse {
  success: boolean;
  results: Array<{ signedContentBase64?: string; errorCode?: string; errorMessage?: string }>;
  errorMessage?: string;
}

/** Token físico detectado (GET /api/token/status). */
export interface TokenStatusItem {
  slotId: number;
  label: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  isSoPinLocked: boolean;
  isUserPinLocked: boolean;
}

/** Respuesta GET /api/token/status. */
export interface TokenStatusResponse {
  success: boolean;
  message: string;
  present: boolean;
  multipleTokensPresent: boolean;
  tokens: TokenStatusItem[];
  errorCode: string | null;
  errorMessage: string | null;
}

/** Respuesta POST /api/token/reset-pin. */
export interface ResetPinResponse {
  success: boolean;
  message: string;
  errorCode: string | null;
  errorMessage: string | null;
}

/** Certificado devuelto por POST /api/token/content. */
export interface TokenContentCertificate {
  thumbprint: string;
  subject: string;
  issuer: string;
  serialNumber: string;
  notBefore: string;
  notAfter: string;
  label: string;
}

/** Detalle del token en respuesta de content. */
export interface TokenContentTokenDetails {
  label: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  totalMemory?: number;
  freeMemory?: number;
  maxPinLen?: number;
  minPinLen?: number;
  hardwareVersion?: string;
  firmwareVersion?: string;
}

/** Respuesta POST /api/token/content. */
export interface TokenContentResponse {
  success: boolean;
  message: string;
  errorCode: string | null;
  errorMessage: string | null;
  tokenDetails: TokenContentTokenDetails | null;
  certificates: TokenContentCertificate[];
  keys: Array<{ label: string; type: string }>;
}

/** Respuesta POST /api/token/import-pfx (flujo Descargar certificado). */
export interface ImportPfxResponse {
  success: boolean;
  message?: string;
  errorCode?: string;
  errorMessage?: string;
  output?: string;
}

/**
 * Servicio para comunicarse con el agente local (complemento de firma con token físico).
 * Comprueba disponibilidad vía GET /health, listado de certificados y firma PAdES/CAdES.
 */
@Injectable({
  providedIn: 'root'
})
export class LocalAgentService {
  private readonly baseUrl = environment.localAgentBaseUrl;
  private readonly healthTimeout = environment.localAgentHealthTimeout ?? 5000;

  constructor(
    private cipher: CipherService,
    private uiHelper: UiHelperService
  ) {}

  private buildUrl(path: string): string {
    return `${this.baseUrl.replace(/\/$/, '')}${path}`;
  }

  private agentRequest<T>(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
    timeoutMs?: number
  ): Observable<T> {
    const url = this.buildUrl(path);
    const effectiveTimeout = timeoutMs ?? this.healthTimeout;
    const isHealthProbe = path === '/health' || path.startsWith('/health?');

    return from(this.executeAgentRequest<T>(method, url, body, effectiveTimeout)).pipe(
      catchError((err) => {
        // Health/poll no debe tumbar el spinner de otra operación en curso.
        if (!isHealthProbe) {
          try {
            this.uiHelper.unlockUi();
          } catch {
            // ignore
          }
        }
        return throwError(() => err);
      })
    );
  }

  /** POST cifrado hacia el agente (TokenSafe y flujos similares). */
  postToAgent<T>(path: string, body: unknown, timeoutMs?: number): Observable<T> {
    return this.agentRequest<T>('POST', path, body, timeoutMs);
  }

  private async executeAgentRequest<T>(
    method: 'GET' | 'POST',
    url: string,
    body: unknown | undefined,
    timeoutMs: number
  ): Promise<T> {
    let requestBody: string | undefined;
    const headers: Record<string, string> = {};

    if (method === 'POST' && body != null) {
      headers['Content-Type'] = 'application/json';
      if (this.cipher.isEncryptionEnabled) {
        const encrypted = await this.cipher.encrypt(JSON.stringify(body));
        requestBody = JSON.stringify({ data: encrypted });
      } else {
        requestBody = JSON.stringify(body);
      }
    }

    const response = await localAgentFetch(url, {
      method,
      headers,
      body: requestBody,
      timeoutMs,
    });

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      json = null;
    }

    if (!response.ok) {
      const errBody = json as { errorMessage?: string; message?: string } | null;
      throw {
        error: errBody,
        message: errBody?.errorMessage || errBody?.message || `HTTP ${response.status}`,
      };
    }

    const payload = json as { data?: string } | null;
    if (payload?.data && typeof payload.data === 'string' && this.cipher.isEncryptionEnabled) {
      const plain = await this.cipher.decrypt(payload.data);
      return JSON.parse(plain) as T;
    }

    return json as T;
  }

  /**
   * URL absoluta del agente para un path relativo (p. ej. `/api/certitoken/certificates`).
   */
  resolveAbsoluteUrl(path: string): string {
    const base = this.baseUrl.replace(/\/$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
  }

  /**
   * Comprueba si el agente local está disponible (health check).
   * GET {baseUrl}/health; considera disponible si status 200 y body.status === 'healthy'.
   * Si `tokenFisicoEnabled` es false, no llama al agente.
   */
  isAvailable(): Observable<boolean> {
    if (!environment.tokenFisicoEnabled) {
      return of(false);
    }
    return this.agentRequest<LocalAgentHealthResponse>('GET', '/health').pipe(
      map(res => res?.status === 'healthy'),
      catchError(() => from(Promise.resolve(false)))
    );
  }

  /**
   * Obtiene la lista de certificados del agente.
   * GET {baseUrl}/api/certificates → { success, certificates: [...] }.
   * @param source Si es 'usb_token', añade ?source=usb_token para listar solo certificados del token físico.
   */
  getCertificates(source?: LocalAgentCertificateSource): Observable<LocalAgentCertificate[]> {
    let path = '/api/certificates';
    if (source === 'usb_token') {
      path += '?source=usb_token';
    }
    return this.agentRequest<LocalAgentCertificatesResponse>('GET', path).pipe(
      map(res => {
        if (res?.success && Array.isArray(res.certificates)) {
          return res.certificates;
        }
        throw new Error(res?.errorMessage || 'Formato de respuesta inválido');
      }),
      catchError(err => {
        const msg = err?.error?.errorMessage || err?.message || 'Error al obtener certificados';
        throw new Error(msg);
      })
    );
  }

  /**
   * Firma uno o varios PDFs (PAdES) con token físico.
   * POST {baseUrl}{agentFisicoSignPdfPath} con { thumbprint, documents: [ { pdfBase64, signReason?, signLocation? } ] }.
   * Retorna array de base64 de los PDFs firmados.
   */
  signPdf(thumbprint: string, documents: LocalAgentPdfDocument[]): Observable<string[]> {
    if (!documents?.length) {
      throw new Error('Se requiere al menos un documento');
    }
    return this.agentRequest<LocalAgentSignPdfResponse>(
      'POST',
      environment.agentFisicoSignPdfPath,
      { thumbprint, documents }
    ).pipe(
      map(res => {
        if (!res?.results?.length) {
          throw new Error(res?.errorMessage || 'Error al firmar PDF');
        }
        const out: string[] = [];
        for (let i = 0; i < res.results.length; i++) {
          const b64 = res.results[i]?.signedPdfBase64;
          if (b64) {
            out.push(b64);
          } else {
            throw new Error(res.results[i]?.errorMessage || `Error al firmar documento ${i + 1}`);
          }
        }
        return out;
      }),
      catchError(err => {
        const msg = err?.error?.errorMessage || err?.message || 'Error al firmar PDF';
        throw new Error(msg);
      })
    );
  }

  /**
   * Firma uno o varios documentos en CAdES con token físico.
   * POST {baseUrl}{agentFisicoSignCadesPath} con { thumbprint, documents: [ { contentBase64, fileName? } ] }.
   * Retorna array de base64 del contenido firmado (.p7z).
   */
  signCades(thumbprint: string, documents: LocalAgentCadesDocument[]): Observable<string[]> {
    if (!documents?.length) {
      throw new Error('Se requiere al menos un documento');
    }
    return this.agentRequest<LocalAgentSignCadesResponse>(
      'POST',
      environment.agentFisicoSignCadesPath,
      { thumbprint, documents }
    ).pipe(
      map(res => {
        if (!res?.success || !res?.results?.length) {
          throw new Error(res?.errorMessage || 'Error al firmar CAdES');
        }
        const out: string[] = [];
        for (let i = 0; i < res.results.length; i++) {
          const b64 = res.results[i]?.signedContentBase64;
          if (b64) {
            out.push(b64);
          } else {
            throw new Error(res.results[i]?.errorMessage || `Error al firmar documento ${i + 1}`);
          }
        }
        return out;
      }),
      catchError(err => {
        const msg = err?.error?.errorMessage || err?.message || 'Error al firmar CAdES';
        throw new Error(msg);
      })
    );
  }

  /**
   * Obtiene el estado de los tokens físicos conectados.
   * GET {baseUrl}/api/token/status
   */
  getTokenStatus(): Observable<TokenStatusResponse> {
    return this.agentRequest<TokenStatusResponse>('GET', '/api/token/status').pipe(
      catchError(err => {
        const msg = err?.error?.errorMessage || err?.message || 'Error al consultar estado del token';
        throw new Error(msg);
      })
    );
  }

  /**
   * Restablece el PIN de usuario de un token.
   * POST {baseUrl}/api/token/reset-pin con { slotId, newUserPin }.
   */
  resetPin(slotId: number, newUserPin: string): Observable<ResetPinResponse> {
    return this.agentRequest<ResetPinResponse>('POST', '/api/token/reset-pin', { slotId, newUserPin }).pipe(
      catchError(err => {
        const body = err?.error;
        const msg = body?.errorMessage || body?.message || err?.message || 'Error al restablecer PIN';
        throw new Error(msg);
      })
    );
  }

  /**
   * Consulta el contenido del token (certificados, etc.) con el PIN de usuario.
   * POST {baseUrl}/api/token/content con { userPin, slotId? }.
   */
  getTokenContent(userPin: string, slotId?: number): Observable<TokenContentResponse> {
    const body = slotId != null ? { userPin, slotId } : { userPin };
    return this.agentRequest<TokenContentResponse>('POST', '/api/token/content', body).pipe(
      catchError(err => {
        const bodyErr = err?.error;
        const msg = bodyErr?.message || bodyErr?.errorMessage || err?.message || 'Error al consultar contenido del token';
        throw new Error(msg);
      })
    );
  }

  /**
   * Importa el certificado (ya descargado/guardado) al token físico.
   * POST {baseUrl}/api/token/import-pfx — userPin, numSolicitud, transacion y token obligatorios.
   */
  importPfx(params: {
    userPin: string;
    numSolicitud: string;
    transacion: string;
    token: string;
  }): Observable<ImportPfxResponse> {
    const userPin = (params.userPin ?? '').trim();
    const numSolicitud = (params.numSolicitud ?? '').trim();
    const transacion = (params.transacion ?? '').trim();
    const token = (params.token ?? '').trim();
    if (!userPin) return throwError(() => new Error('El PIN de usuario del token (userPin) es obligatorio'));
    if (!numSolicitud) return throwError(() => new Error('El número de solicitud es obligatorio'));
    if (!transacion) return throwError(() => new Error('El identificador de transacción es obligatorio'));
    if (!token) return throwError(() => new Error('El token de sesión es obligatorio'));
    const body = { userPin, numSolicitud, transacion, token };
    return this.agentRequest<ImportPfxResponse>('POST', '/api/token/import-pfx', body, 120000).pipe(
      catchError(err => {
        const bodyErr = err?.error;
        const msg = bodyErr?.errorMessage || bodyErr?.message || err?.message || 'Error al importar en token';
        throw new Error(msg);
      })
    );
  }
}
